// Visual-type taxonomy for script rows.
//
// Every VISUAL script cell can carry a `visualType` attribute describing what
// appears in frame: talent, B-roll, text, graphics or a screen capture. It
// rides inside the doc_json as a ProseMirror node attribute, so the worker
// and the client editor can share one source of truth.
//
// `null` means "untyped": existing rows authored before this feature, and any
// row the producer hasn't tagged yet. DEFAULT_VISUAL_TYPE defines the type
// for new rows when seeding is introduced.
//
// Framework-free so the worker and the client editor can use it directly.

export type VisualType = 'talent' | 'broll' | 'text' | 'graphics' | 'screen';

export const VISUAL_TYPES: readonly VisualType[] = [
  'talent',
  'broll',
  'text',
  'graphics',
  'screen',
];

/** The default type for a brand-new row. Untagged existing rows stay
 *  `null` (untyped), as described in the module note above. */
export const DEFAULT_VISUAL_TYPE: VisualType = 'broll';

export interface VisualTypeMeta {
  type: VisualType;
  /** Short label shown on the chip and in the picker. */
  label: string;
  /** One-line description for the picker subtitle and chip tooltip. */
  gloss: string;
}

export const VISUAL_TYPE_META: Record<VisualType, VisualTypeMeta> = {
  talent: {
    type: 'talent',
    label: 'Talent on screen',
    gloss: 'A presenter, interviewee or actor is in frame.',
  },
  broll: {
    type: 'broll',
    label: 'B-roll',
    gloss: 'Cutaway footage of the place, the people, the product.',
  },
  text: {
    type: 'text',
    label: 'Text on screen',
    gloss: 'Titles, captions or a lower third carry the frame.',
  },
  graphics: {
    type: 'graphics',
    label: 'Graphics',
    gloss: 'Motion graphics, animation, a diagram or a logo sting.',
  },
  screen: {
    type: 'screen',
    label: 'Screen capture',
    gloss: 'Product UI, an app or a website recording.',
  },
};

export function isVisualType(value: unknown): value is VisualType {
  return typeof value === 'string' && (VISUAL_TYPES as readonly string[]).includes(value);
}

/** Coerce an unknown attribute value to a valid VisualType, or `null` (untyped)
 *  for anything unrecognised, including the empty string and legacy docs. */
export function normalizeVisualType(value: unknown): VisualType | null {
  return isVisualType(value) ? value : null;
}
